import { formatMoney } from '@transferwise/formatting';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';

import DeliveryEstimation from '../../common/DeliveryEstimation';
import ExchangeRateIndicator from '../../common/ExchangeRateIndicator';
import TransferFee from '../../common/TransferFee';
import createClassNamesFromStyles from '../../resources/createClassNamesFromStyles';
import { getCurrencyForCode } from '../../resources/currencies';
import styles from '../ProviderComparisonTable.module.less';

import SendWithTw from './SendWithTw';
import withQuoteComparison from './withQuoteComparison';

const createClassNames = createClassNamesFromStyles(styles);

const SendComparison = withQuoteComparison(SendWithTw);

const RouteRow = React.forwardRef(
  (
    {
      name,
      alias,
      logo,
      baseProviderQuote,
      comparedProviderQuote,
      showDeliveryEstimates,
      sendAmount,
      isCentre,
      onCallToActionClick,
    },
    reference,
  ) => {
    const { formatMessage, locale } = useIntl();
    const { sourceCurrency, targetCurrency, deliveryEstimation, fee, rate, receivedAmount } =
      baseProviderQuote;

    const currencyNameId = getCurrencyForCode(targetCurrency).nameTranslationKey;
    const currencyName = currencyNameId && formatMessage({ id: currencyNameId });

    return (
      <tr
        className={styles['table-plain__row']}
        data-testid={isCentre ? 'centre-route-row' : 'route-row'}
        ref={reference}
      >
        {/* eslint-disable-next-line sonarjs/no-duplicate-string */}
        <td className={createClassNames(['table-plain__cell', 'table-plain__cell--currency'])}>
          <div className={createClassNames(['Currency'])}>
            <div className={createClassNames(['Currency__container'])}>
              <i
                className={`${createClassNames([
                  'Currency__flag',
                ])} currency-flag currency-flag-lg currency-flag-${targetCurrency.toLowerCase()}`}
              />
              <span className={createClassNames(['Currency__acronym'])}>{targetCurrency}</span>
            </div>
            <span className={createClassNames(['Currency__name'])}>{currencyName}</span>
          </div>
        </td>
        <td
          className={createClassNames([
            'table-plain__cell',
            'table-plain__cell--provider',
            'only-mobile',
          ])}
        >
          {logo && (
            <img
              className={createClassNames(['ProviderLogo'])}
              src={logo}
              alt={formatMessage({ id: 'provider-logo-alt-text' }, { name })}
            />
          )}
        </td>
        {showDeliveryEstimates && (
          <td
            className={styles['table-plain__cell']}
            data-label={formatMessage({ id: 'should-arrive' })}
          >
            {deliveryEstimation && (
              <DeliveryEstimation
                alias={alias}
                onClickingDeliveryEstimationTooltip={() => {}}
                deliveryEstimation={deliveryEstimation}
                isPartner={false}
                styles={styles}
              />
            )}
          </td>
        )}
        <td
          className={styles['table-plain__cell']}
          data-label={formatMessage({ id: 'transfer-fee' })}
        >
          <TransferFee currency={sourceCurrency} fee={fee} />
        </td>
        <td
          className={styles['table-plain__cell']}
          data-label={formatMessage({ id: 'exchange-rate' })}
        >
          <ExchangeRateIndicator
            rate={rate}
            sourceCurrency={sourceCurrency}
            targetCurrency={targetCurrency}
          />
        </td>
        <td
          className={createClassNames(['table-plain__cell', 'table-plain__cell--received-amount'])}
          data-testid="received-amount"
        >
          <span className={styles.receiveText}>
            {formatMoney(receivedAmount, targetCurrency, locale)}
          </span>
        </td>
        <td className={createClassNames(['table-plain__cell', 'featured--last-cell'])}>
          <SendComparison
            baseProviderQuote={baseProviderQuote}
            comparedProviderQuote={comparedProviderQuote}
            showDeliveryEstimates={showDeliveryEstimates}
            sendAmount={sendAmount}
            onCallToActionClick={onCallToActionClick}
          />
        </td>
      </tr>
    );
  },
);

RouteRow.propTypes = {
  name: PropTypes.string.isRequired,
  alias: PropTypes.string.isRequired,
  logo: PropTypes.string,
  baseProviderQuote: PropTypes.shape({
    sourceCurrency: PropTypes.string.isRequired,
    targetCurrency: PropTypes.string.isRequired,
    deliveryEstimation: PropTypes.shape(DeliveryEstimation.propTypes.deliveryEstimation).isRequired,
    fee: PropTypes.number.isRequired,
    rate: PropTypes.number.isRequired,
    receivedAmount: PropTypes.number.isRequired,
  }).isRequired,
  comparedProviderQuote: PropTypes.shape({}).isRequired,
  showDeliveryEstimates: PropTypes.bool,
  sendAmount: PropTypes.number,
  isCentre: PropTypes.bool,
};

RouteRow.defaultProps = {
  logo: '',
  showDeliveryEstimates: false,
  sendAmount: 0,
  isCentre: false,
};

export default RouteRow;
